import { useState } from "react";
import { Plus, X, GripVertical, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useCreateContractTemplate, ContractSection, MergeField } from "@/hooks/useContractTemplates";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { ClauseLibrary, Clause } from "./ClauseLibrary";

const defaultSections: ContractSection[] = [
  { id: "1", name: "Executive Summary", content: "{{company_name}} proposes to provide {{service_description}} to {{client_name}}.", order: 0 },
  { id: "2", name: "Scope of Work", content: "The services covered under this contract include:\n\n{{scope_items}}", order: 1 },
  { id: "3", name: "Terms & Conditions", content: "This contract is valid for {{contract_duration}}. Payment terms: {{payment_terms}}.", order: 2 },
  { id: "4", name: "Pricing", content: "Total contract value: {{total_amount}}\n\nPayment schedule:\n{{payment_schedule}}", order: 3 },
];

const defaultMergeFields: MergeField[] = [
  { key: "company_name", label: "Company Name", type: "text" },
  { key: "client_name", label: "Client Name", type: "text" },
  { key: "service_description", label: "Service Description", type: "text" },
  { key: "scope_items", label: "Scope Items", type: "text" },
  { key: "contract_duration", label: "Contract Duration", type: "text" },
  { key: "payment_terms", label: "Payment Terms", type: "text" },
  { key: "total_amount", label: "Total Amount", type: "currency" },
  { key: "payment_schedule", label: "Payment Schedule", type: "text" },
];

export function AddContractTemplateDialog() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("general");
  const [sections, setSections] = useState<ContractSection[]>(defaultSections);
  const [mergeFields, setMergeFields] = useState<MergeField[]>(defaultMergeFields);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const createTemplate = useCreateContractTemplate();

  const handleAddSection = () => {
    const newSection: ContractSection = {
      id: crypto.randomUUID(),
      name: "New Section",
      content: "",
      order: sections.length,
    };
    setSections([...sections, newSection]);
  };

  const handleUpdateSection = (id: string, field: keyof ContractSection, value: string | number) => {
    setSections(sections.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
  };

  const handleRemoveSection = (id: string) => {
    setSections(sections.filter((s) => s.id !== id));
  };

  const handleAddMergeField = () => {
    const newField: MergeField = {
      key: `field_${mergeFields.length + 1}`,
      label: "New Field",
      type: "text",
    };
    setMergeFields([...mergeFields, newField]);
  };

  const handleUpdateMergeField = (key: string, field: keyof MergeField, value: string) => {
    setMergeFields(
      mergeFields.map((f) =>
        f.key === key ? { ...f, [field]: value, key: field === "label" ? value.toLowerCase().replace(/\s+/g, "_") : f.key } : f
      )
    );
  };

  const handleRemoveMergeField = (key: string) => {
    setMergeFields(mergeFields.filter((f) => f.key !== key));
  };

  const handleInsertClause = (clause: Clause) => {
    if (activeSectionId) {
      setSections(sections.map((s) =>
        s.id === activeSectionId
          ? { ...s, content: s.content + (s.content ? "\n\n" : "") + clause.content }
          : s
      ));
    } else {
      // Add as new section
      const newSection: ContractSection = {
        id: crypto.randomUUID(),
        name: clause.name,
        content: clause.content,
        order: sections.length,
      };
      setSections([...sections, newSection]);
    }
  };

  const handleSubmit = () => {
    createTemplate.mutate(
      {
        name,
        description: description || undefined,
        category,
        sections,
        merge_fields: mergeFields,
      },
      {
        onSuccess: () => {
          setOpen(false);
          setName("");
          setDescription("");
          setCategory("general");
          setSections(defaultSections);
          setMergeFields(defaultMergeFields);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          New Template
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Contract Template</DialogTitle>
          <DialogDescription>
            Define reusable sections and merge fields for faster contract generation
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Template Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Standard Service Agreement"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="service">Service Agreement</SelectItem>
                  <SelectItem value="software">Software License</SelectItem>
                  <SelectItem value="consulting">Consulting</SelectItem>
                  <SelectItem value="nda">NDA</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of when to use this template"
              rows={2}
            />
          </div>

          <Tabs defaultValue="sections" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="sections">Sections ({sections.length})</TabsTrigger>
              <TabsTrigger value="fields">Merge Fields ({mergeFields.length})</TabsTrigger>
              <TabsTrigger value="clauses">
                <BookOpen className="h-4 w-4 mr-1" />
                Clause Library
              </TabsTrigger>
            </TabsList>

            <TabsContent value="sections" className="space-y-3 mt-4">
              <p className="text-sm text-muted-foreground">
                Click on a section to make it active, then insert clauses from the Clause Library tab.
              </p>
              {sections.map((section, index) => (
                <Card 
                  key={section.id} 
                  className={`cursor-pointer transition-all ${activeSectionId === section.id ? 'ring-2 ring-primary' : ''}`}
                  onClick={() => setActiveSectionId(section.id)}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <GripVertical className="h-5 w-5 text-muted-foreground mt-2 cursor-move" />
                      <div className="flex-1 space-y-3">
                        <Input
                          value={section.name}
                          onChange={(e) => handleUpdateSection(section.id, "name", e.target.value)}
                          placeholder="Section name"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Textarea
                          value={section.content}
                          onChange={(e) => handleUpdateSection(section.id, "content", e.target.value)}
                          placeholder="Section content (use {{field_name}} for merge fields)"
                          rows={3}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveSection(section.id);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              <Button variant="outline" onClick={handleAddSection} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Section
              </Button>
            </TabsContent>

            <TabsContent value="fields" className="space-y-3 mt-4">
              <p className="text-sm text-muted-foreground mb-3">
                Use merge fields in sections with {"{{field_key}}"} syntax
              </p>
              {mergeFields.map((field) => (
                <div key={field.key} className="flex items-center gap-3">
                  <Input
                    value={field.label}
                    onChange={(e) => handleUpdateMergeField(field.key, "label", e.target.value)}
                    placeholder="Field label"
                    className="flex-1"
                  />
                  <code className="text-xs bg-muted px-2 py-1 rounded min-w-[120px]">
                    {`{{${field.key}}}`}
                  </code>
                  <Select
                    value={field.type}
                    onValueChange={(v) => handleUpdateMergeField(field.key, "type", v)}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">Text</SelectItem>
                      <SelectItem value="number">Number</SelectItem>
                      <SelectItem value="date">Date</SelectItem>
                      <SelectItem value="currency">Currency</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveMergeField(field.key)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" onClick={handleAddMergeField} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Merge Field
              </Button>
            </TabsContent>

            <TabsContent value="clauses" className="mt-4">
              <ClauseLibrary mode="select" onInsertClause={handleInsertClause} />
              {activeSectionId && (
                <p className="text-sm text-primary mt-2">
                  ✓ Inserting into: {sections.find(s => s.id === activeSectionId)?.name}
                </p>
              )}
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!name || createTemplate.isPending}>
            {createTemplate.isPending ? "Creating..." : "Create Template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
